import { useCallback, useEffect, useMemo, useState } from "react";
import { directorioMiembros, guardarVarias, listaNinos, listar, unir } from "../../services/datos";
import QrRegistro from "../registro/QrRegistro";
import { botonPequeno, botonPrimario, campo, etiqueta, fechaLarga, hora, hoyISO, nombreReunion } from "../../shared/estilos";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";

// Público de cada reunión según su tipo (niños, hombres, mujeres o todos).
export function categoriaDe(servicio) {
  const tipo = servicio?.tipos_servicio;
  if (!tipo) return null;
  const dirigido = (tipo.dirigido_a || "").toLowerCase();
  const nombre = (tipo.nombre || "").toLowerCase();
  if (["ninos", "niños"].includes(dirigido) || nombre.includes("niño") || nombre.includes("infantil")) return "ninos";
  if (dirigido === "hombres" || nombre.includes("hombre")) return "hombres";
  if (["mujeres", "damas"].includes(dirigido) || nombre.includes("mujer") || nombre.includes("dama")) return "mujeres";
  return null;
}

// Asistencia: solo se registra quién vino. Las reuniones se crean en Calendario.
function Asistencia({ contexto, irA }) {
  const [servicios, setServicios] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [ninos, setNinos] = useState([]);
  const [servicioId, setServicioId] = useState(contexto?.servicioId ? String(contexto.servicioId) : "");
  const [asistencia, setAsistencia] = useState({});
  const [guardada, setGuardada] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [mostrandoQr, setMostrandoQr] = useState(false);
  const hoy = hoyISO();

  useEffect(() => {
    Promise.all([listar("servicios", { select: "*, tipos_servicio(nombre, dirigido_a)", orden: "fecha" }), directorioMiembros(), listaNinos()])
      .then(([listaServicios, listaMiembros, ninosSinTutor]) => {
        // El tutor de cada niño, con su nombre (para buscar).
        const listaNinos = unir(ninosSinTutor, "tutor_id", listaMiembros, "miembros");
        const clave = (s) => `${s.fecha}${s.hora || ""}`;
        // Próximas (hoy incluido) de la más cercana a la más lejana; pasadas de la más reciente hacia atrás.
        const proximas = listaServicios.filter((s) => s.fecha >= hoy).sort((a, b) => clave(a).localeCompare(clave(b)));
        const pasadas = listaServicios.filter((s) => s.fecha < hoy).sort((a, b) => clave(b).localeCompare(clave(a)));
        setServicios([...proximas, ...pasadas]);
        setMiembros(listaMiembros);
        setNinos(listaNinos);
        // Por defecto: la reunión de hoy o, si no hay, la próxima más cercana (o la última realizada).
        const inicial = proximas[0] || pasadas[0];
        setServicioId((actual) => actual || (inicial ? String(inicial.id) : ""));
      })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [hoy]);

  const servicio = servicios.find((s) => String(s.id) === servicioId);
  const categoria = categoriaDe(servicio);
  const esFutura = Boolean(servicio) && servicio.fecha > hoy;
  const proximas = servicios.filter((s) => s.fecha >= hoy);
  const pasadas = servicios.filter((s) => s.fecha < hoy);
  const opcion = (s) => (
    <option key={s.id} value={s.id}>
      {s.fecha === hoy ? "Hoy" : fechaLarga(s.fecha, { weekday: "short", day: "numeric", month: "short" })} · {nombreReunion(s)}
    </option>
  );

  const cargarAsistencia = useCallback(async () => {
    if (!servicio) return;
    setMensaje("");
    const tabla = categoria === "ninos" ? "asistencia_ninos" : "asistencia_adultos";
    try {
      const registros = await listar(tabla, { filtros: { servicio_id: servicio.id } });
      const mapa = Object.fromEntries(registros.map((a) => [categoria === "ninos" ? a.nino_id : a.miembro_id, a.presente]));
      setAsistencia(mapa);
      setGuardada(mapa);
    } catch (e) {
      setError(e.message);
    }
  }, [servicio, categoria]);

  useEffect(() => { cargarAsistencia(); }, [cargarAsistencia]);

  const participantes = useMemo(() => {
    if (categoria === "ninos") return ninos;
    const activos = miembros.filter((m) => !["baja", "inactivo"].includes((m.estado || "").toLowerCase()));
    if (categoria === "hombres") return activos.filter((m) => ["masculino", "hombre"].includes((m.genero || "").toLowerCase()));
    if (categoria === "mujeres") return activos.filter((m) => ["femenino", "mujer", "dama"].includes((m.genero || "").toLowerCase()));
    return activos;
  }, [categoria, miembros, ninos]);

  const filtrados = participantes.filter((p) => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return true;
    const tutor = p.miembros ? `${p.miembros.nombres || ""} ${p.miembros.apellidos || ""}` : "";
    return `${p.nombres || ""} ${p.apellidos || ""} ${p.email || ""} ${tutor}`.toLowerCase().includes(texto);
  });

  const presentes = participantes.filter((p) => asistencia[p.id]).length;
  const hayCambios = participantes.some((p) => Boolean(asistencia[p.id]) !== Boolean(guardada[p.id]));

  const marcarTodos = (valor) =>
    setAsistencia((prev) => ({ ...prev, ...Object.fromEntries(filtrados.map((p) => [p.id, valor])) }));

  const cambiarReunion = async (id) => {
    if (hayCambios && !(await confirmar("Hay cambios sin guardar en esta asistencia. ¿Cambiar de reunión de todas formas?", { aceptar: "Cambiar de reunión" }))) return;
    setAsistencia({});
    setGuardada({});
    setServicioId(id);
  };

  const guardar = async () => {
    setGuardando(true);
    setError("");
    const campoPersona = categoria === "ninos" ? "nino_id" : "miembro_id";
    const registros = participantes
      .filter((p) => p.id in asistencia || p.id in guardada)
      .map((p) => ({ servicio_id: servicio.id, [campoPersona]: p.id, presente: Boolean(asistencia[p.id]) }));
    try {
      // Guardar otra vez actualiza en lugar de duplicar.
      await guardarVarias(categoria === "ninos" ? "asistencia_ninos" : "asistencia_adultos", registros, `servicio_id,${campoPersona}`);
      setGuardada({ ...asistencia });
      setMensaje(`Asistencia guardada: ${presentes} presente(s).`);
    } catch (e) {
      setError(e.message || "No se pudo guardar la asistencia");
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <Esqueleto className="p-6" />;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="titulo-pagina">Asistencia</h2>
        <p className="text-gray-400 text-sm mt-1">Marca quién vino a cada reunión. Las reuniones se crean en Calendario.</p>
      </div>

      {servicios.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-700 p-12 text-center">
          <p className="font-semibold text-white">Aún no hay reuniones</p>
          <p className="mt-2 text-sm text-gray-400">Primero crea la reunión en Calendario y aparecerá aquí.</p>
          <button onClick={() => irA("reuniones")} className={`${botonPrimario} mt-6`}>Ir a Calendario</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <aside className="panel p-4">
            <label htmlFor="asistencia-reunion" className={etiqueta}>Reunión</label>
            <Selector id="asistencia-reunion" value={servicioId} onChange={(e) => cambiarReunion(e.target.value)} className={`${campo} mt-2`}>
              {proximas.length > 0 && <optgroup label="Próximas">{proximas.map(opcion)}</optgroup>}
              {pasadas.length > 0 && <optgroup label="Pasadas">{pasadas.map(opcion)}</optgroup>}
            </Selector>
            {servicio && (
              <div className="mt-6">
                <p className="text-white font-semibold">{nombreReunion(servicio)}</p>
                <p className="mt-1 text-sm capitalize text-gray-400">
                  {fechaLarga(servicio.fecha)}{servicio.hora ? ` · ${hora(servicio.hora)}` : ""}
                </p>
                <p className="mt-6 text-5xl font-bold text-white">{presentes}</p>
                <p className="text-sm text-gray-400">de {participantes.length} {categoria === "ninos" ? "niños" : "personas"} presentes</p>
                <div className="mt-6 flex flex-col gap-2">
                  {servicio.fecha >= hoy && (
                    <button onClick={() => setMostrandoQr(true)} className={botonPrimario}>
                      {servicio.fecha === hoy ? "Mostrar QR de registro" : "Preparar QR para imprimir"}
                    </button>
                  )}
                  <button onClick={cargarAsistencia} disabled={hayCambios} className={botonPequeno}
                    title={hayCambios ? "Guarda primero tus cambios" : "Ver quién se ha registrado con el QR"}>
                    Actualizar registros
                  </button>
                  <button onClick={() => irA("reuniones")} className={botonPequeno}>¿Falta una reunión? Calendario</button>
                </div>
              </div>
            )}
          </aside>

          <section className="panel p-4 lg:col-span-2">
            {esFutura && (
              <p className="mb-4 rounded border border-white/15 bg-white/5 p-3 text-sm text-gray-300">
                Esta reunión aún no ha pasado. La asistencia se marca el día de la reunión; mientras tanto puedes preparar el QR.
              </p>
            )}
            <fieldset disabled={esFutura} className={esFutura ? "opacity-40" : ""}>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row">
              <input type="search" placeholder="Buscar por nombre, correo o tutor..." value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)} className={`${campo} flex-1`} aria-label="Buscar persona" />
              <div className="flex gap-2">
                <button onClick={() => marcarTodos(true)} className={botonPequeno}>Marcar todos</button>
                <button onClick={() => marcarTodos(false)} className={botonPequeno}>Limpiar</button>
              </div>
            </div>

            <ul className="max-h-[28rem] space-y-1.5 overflow-y-auto pr-1">
              {filtrados.map((p) => {
                const presente = Boolean(asistencia[p.id]);
                return (
                  <li key={p.id}>
                    <button onClick={() => setAsistencia((prev) => ({ ...prev, [p.id]: !presente }))} aria-pressed={presente}
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${presente ? "border-white/40 bg-white/5" : "border-gray-700 hover:border-gray-500"}`}>
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${presente ? "border-white bg-white text-black" : "border-gray-600"}`}>
                        {presente ? "✓" : ""}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-white">{p.nombres} {p.apellidos}</span>
                        <span className="block text-xs text-gray-500">
                          {p.miembros ? `Tutor: ${p.miembros.nombres} ${p.miembros.apellidos}` : p.estado || ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {filtrados.length === 0 && <li className="p-4 text-sm text-gray-500">No hay personas con ese nombre.</li>}
            </ul>

            {error && <p role="alert" className="mt-4 text-sm text-red-300">{error}</p>}
            {mensaje && !hayCambios && <p className="mt-4 text-sm text-gray-300">{mensaje}</p>}
            <button onClick={guardar} disabled={guardando || !hayCambios} className={`${botonPrimario} mt-4 w-full`}>
              {guardando ? "Guardando..." : hayCambios ? "Guardar asistencia" : "Sin cambios por guardar"}
            </button>
            </fieldset>
          </section>
        </div>
      )}
      {mostrandoQr && servicio && <QrRegistro servicio={servicio} onCerrar={() => setMostrandoQr(false)} />}
    </div>
  );
}

export default Asistencia;
